/**
 * LEGADO — fora do menu desde 25/09/2026.
 *
 * Substituída por `LeadsDashboard` (/meta/leads-dashboard), que virou "Leads".
 * A rota /meta/leads continua registrada em App.tsx porque há links salvos e
 * relatórios antigos apontando para cá.
 *
 * POR QUE SAIU
 * As duas telas mediam a mesma coisa e discordavam em quase todo número. A
 * nova apura receita MEDIDA por utm_campaign (esta rateava), tira página
 * pausada do estoque (esta somava, e 400 páginas desligadas respondiam por 56%
 * do denominador) e separa flow de broadcast, que têm métricas diferentes —
 * flow capta o lead, broadcast só reativa quem já está na base.
 *
 * Manter as duas visíveis produzia a pergunta "qual está certa?" toda semana.
 *
 * Não evoluir esta tela. Correção que apareça aqui provavelmente já está
 * resolvida na nova — conferir lá antes de mexer.
 */
import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getLeads, getLeadStats, getMetaPages, reactivateLeads } from '@/services/api';
import { LeadStats, LeadTable, ViewLeadModal, SyncLeadsDialog, FloatingSyncStatus } from './components';
import { Lead, LeadFilters, LeadResponse, DayRange } from '@/types/meta-lead.ts';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { RefreshCw, Search, X, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

type Tab = 'dashboard' | 'leads';

const triStateToBool = (v?: 'all' | 'yes' | 'no') =>
    v === 'yes' ? true : v === 'no' ? false : undefined;

function useDebounce<T>(value: T, ms = 350): T {
    const [d, setD] = useState(value);
    useEffect(() => { const t = setTimeout(() => setD(value), ms); return () => clearTimeout(t); }, [value, ms]);
    return d;
}

type Chip = { id: string; label: string };
const CHIPS: Chip[] = [
    { id: 'in_24h_window', label: 'in_24h_window' },
    { id: 'is_new_today', label: 'is_new_today' },
    { id: 'interacted_today', label: 'interacted_today' },
];

export default function Leads() {
    const queryClient = useQueryClient();
    const [tab, setTab] = useState<Tab>('dashboard');
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false);
    const [activeSyncTaskId, setActiveSyncTaskId] = useState<string | null>(null);
    const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [searchInput, setSearchInput] = useState('');
    const [activeChips, setActiveChips] = useState<Set<string>>(new Set());
    const [filters] = useState<LeadFilters>({ sortBy: 'last_interaction_at', sortOrder: 'desc', in_funnel: 'all', in_24h_window: 'all', is_active: 'all' });
    const [days, setDays] = useState<DayRange>(7);

    const debouncedSearch = useDebounce(searchInput);
    const perPage = 20;

    const activeFilters = useMemo((): LeadFilters => ({
        ...filters,
        search: debouncedSearch || undefined,
        in_24h_window: activeChips.has('in_24h_window') ? 'yes' : filters.in_24h_window,
        new_today: activeChips.has('is_new_today') ? 'yes' : undefined,
        interacted_today: activeChips.has('interacted_today') ? 'yes' : undefined,
    }), [filters, debouncedSearch, activeChips]);

    const { data: metaPagesData } = useQuery({
        queryKey: ['meta-pages'],
        queryFn: getMetaPages,
        staleTime: 5 * 60 * 1000,   // 5 min — lista de páginas muda raramente
        gcTime: 10 * 60 * 1000,
    });

    const { data: statsData } = useQuery({
        queryKey: ['lead-stats', activeFilters, days],
        queryFn: () => getLeadStats({
            in_24h_window: triStateToBool(activeFilters.in_24h_window),
            new_today: triStateToBool(activeFilters.new_today),
            interacted_today: triStateToBool(activeFilters.interacted_today),
            search: activeFilters.search,
            days,
        }),
        placeholderData: prev => prev,
        staleTime: 2 * 60 * 1000,
        refetchInterval: 3 * 60 * 1000,
    });

    const { data: leadsData, isLoading, isFetching, isPlaceholderData: isLeadsStale } = useQuery({
        queryKey: ['leads', currentPage, activeFilters],
        queryFn: () => getLeads({
            page: currentPage, per_page: perPage,
            in_24h_window: triStateToBool(activeFilters.in_24h_window),
            new_today: triStateToBool(activeFilters.new_today),
            interacted_today: triStateToBool(activeFilters.interacted_today),
            search: activeFilters.search,
        }),
        placeholderData: prev => prev,
        staleTime: 60 * 1000,
        refetchInterval: 2 * 60 * 1000,
        enabled: tab === 'leads',
    });

    const leads = useMemo((): Lead[] =>
        (leadsData?.items ?? []).map((r: LeadResponse) => ({
            id: r.id, user_id: r.user_id, sender_id: r.sender_id, page_id: r.page_id,
            page_name: r.page_name, nome: r.nome, sobrenome: r.sobrenome, pais: r.pais,
            cidade: r.cidade, estado: r.estado, sexo: r.sexo, is_active: r.is_active,
            last_error_code: r.last_error_code, last_interaction_at: r.last_interaction_at,
            in_24h_window: r.in_24h_window, current_funnel_id: r.current_funnel_id,
            last_node_id: r.last_node_id, tags: r.tags ?? [], total_clicks: r.total_clicks,
            created_at: r.created_at, updated_at: r.updated_at,
            is_new_today: r.is_new_today, interacted_today: r.interacted_today, is_first_day: r.is_first_day,
        })),
    [leadsData]);

    const reactivateMutation = useMutation({
        mutationFn: (ids: number[]) => reactivateLeads(ids),
        onSuccess: () => {
            setSelectedIds(new Set());
            queryClient.invalidateQueries({ queryKey: ['leads'] });
            queryClient.invalidateQueries({ queryKey: ['lead-stats'] });
        },
    });

    const handleToggleSelect = (id: number) =>
        setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

    const toggleChip = (id: string) =>
        setActiveChips(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); setCurrentPage(1); return n; });

    const totalPages = leadsData?.pages || 1;
    const totalLeadsFound = leadsData?.total || 0;

    const activePageCount = metaPagesData?.filter(p => p.is_active)?.length ?? 0;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
                    <p className="text-sm text-gray-400 mt-0.5">Visão geral baseada nos payloads de páginas, leads e cliques.</p>
                </div>
                <div className="flex items-center gap-2">
                    {/* Tabs */}
                    <div className="flex items-center bg-gray-100 rounded-lg p-1 gap-1">
                        {(['dashboard', 'leads'] as Tab[]).map(t => (
                            <button
                                key={t}
                                onClick={() => setTab(t)}
                                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all capitalize ${t === tab ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                {t === 'dashboard' ? 'Visão Geral' : 'Segmentação'}
                            </button>
                        ))}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setIsSyncDialogOpen(true)} className="gap-1.5 rounded-lg">
                        <RefreshCw className="w-3.5 h-3.5" />
                        Sincronizar
                    </Button>
                </div>
            </div>

            {/* Dashboard tab */}
            {tab === 'dashboard' && (
                <LeadStats
                    totalLeads={statsData?.total_leads}
                    activeLeads={statsData?.active_leads}
                    leadsInFunnel={statsData?.leads_in_funnel}
                    leadsNewToday={statsData?.leads_new_today}
                    leadsInteractedToday={statsData?.leads_interacted_today}
                    leadsIn24h={statsData?.leads_in_24h_window}
                    leadsWebhook={statsData?.leads_webhook}
                    leadsSync={statsData?.leads_sync}
                    totalClicks={statsData?.total_clicks}
                    clicksToday={statsData?.clicks_today}
                    clicksThisWeek={statsData?.clicks_this_week}
                    clicksThisMonth={statsData?.clicks_this_month}
                    clicksFunnel={statsData?.clicks_funnel}
                    clicksBroadcast={statsData?.clicks_broadcast}
                    uniqueLeadsClicked={statsData?.unique_leads_clicked}
                    topLeads={statsData?.top_leads}
                    totalPages={metaPagesData?.length}
                    activePages={activePageCount}
                    leadsReached={statsData?.leads_reached}
                    days={days}
                    onDaysChange={setDays}
                    daily={statsData?.daily ?? []}
                />
            )}

            {/* Leads tab */}
            {tab === 'leads' && (
                <div className="space-y-4">
                    {/* Leads header */}
                    <div className="bg-white border border-gray-200 rounded-xl p-5">
                        <h2 className="text-xl font-bold text-gray-900">Leads</h2>
                        <p className="text-sm text-gray-400 mt-0.5">
                            {totalLeadsFound.toLocaleString('pt-BR')} leads encontrados, página {currentPage} de {totalPages.toLocaleString('pt-BR')}
                        </p>

                        {/* Search + chips */}
                        <div className="flex items-center gap-2 mt-4">
                            <div className="relative w-72">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                                <Input
                                    value={searchInput}
                                    onChange={e => { setSearchInput(e.target.value); setCurrentPage(1); }}
                                    placeholder="Buscar por nome, página, país, funil..."
                                    className="pl-9 h-9 rounded-lg border-gray-200 text-sm bg-gray-50 focus:bg-white"
                                />
                                {searchInput && (
                                    <button onClick={() => setSearchInput('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>

                            {/* Filter chips */}
                            {CHIPS.map(chip => (
                                <button
                                    key={chip.id}
                                    onClick={() => toggleChip(chip.id)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                                        activeChips.has(chip.id)
                                            ? 'bg-blue-600 text-white border-blue-600'
                                            : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
                                    }`}
                                >
                                    {chip.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Bulk bar */}
                    {selectedIds.size > 0 && (
                        <div className="flex items-center justify-between bg-gray-900 text-white rounded-xl px-4 py-3">
                            <span className="text-sm">{selectedIds.size} selecionado{selectedIds.size > 1 ? 's' : ''}</span>
                            <div className="flex gap-2">
                                <Button size="sm" onClick={() => reactivateMutation.mutate(Array.from(selectedIds))} disabled={reactivateMutation.isPending} className="bg-emerald-500 hover:bg-emerald-600 text-white border-0 rounded-lg h-8 gap-1.5">
                                    <CheckCircle className="w-3.5 h-3.5" />
                                    {reactivateMutation.isPending ? 'Reativando...' : 'Visar novamente'}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} className="text-white/60 hover:text-white hover:bg-white/10 rounded-lg h-8">
                                    <X className="w-3.5 h-3.5" />
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Table */}
                    {isLoading && !leadsData ? (
                        <div className="bg-white border border-gray-200 rounded-xl p-12 flex items-center justify-center gap-3 text-gray-400">
                            <div className="w-4 h-4 border-2 border-gray-200 border-t-gray-500 rounded-full animate-spin" />
                            <span className="text-sm">Carregando...</span>
                        </div>
                    ) : (
                        <div className={`transition-opacity ${isLeadsStale ? 'opacity-60' : 'opacity-100'}`}>
                            {leads.length > 0 ? (
                                <>
                                    <LeadTable
                                        leads={leads}
                                        onView={lead => { setSelectedLeadId(lead.id); setIsViewModalOpen(true); }}
                                        selectedIds={selectedIds}
                                        onToggleSelect={handleToggleSelect}
                                    />

                                    {/* Pagination */}
                                    {totalPages > 1 && (
                                        <div className="flex items-center justify-between mt-3">
                                            <p className="text-xs text-gray-400">
                                                {totalLeadsFound.toLocaleString('pt-BR')} leads · página {currentPage} de {totalPages}
                                            </p>
                                            <div className="flex items-center gap-1">
                                                <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => { setCurrentPage(p => p - 1); window.scrollTo({ top: 0 }); }} className="w-8 h-8 p-0 rounded-lg">
                                                    <ChevronLeft className="w-3.5 h-3.5" />
                                                </Button>
                                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                    const pg = currentPage <= 3 ? i + 1 : currentPage >= totalPages - 2 ? totalPages - 4 + i : currentPage - 2 + i;
                                                    if (pg < 1 || pg > totalPages) return null;
                                                    return (
                                                        <Button key={pg} variant={pg === currentPage ? 'default' : 'outline'} size="sm" onClick={() => { setCurrentPage(pg); window.scrollTo({ top: 0 }); }} className="w-8 h-8 p-0 rounded-lg text-xs">
                                                            {pg}
                                                        </Button>
                                                    );
                                                })}
                                                <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => { setCurrentPage(p => p + 1); window.scrollTo({ top: 0 }); }} className="w-8 h-8 p-0 rounded-lg">
                                                    <ChevronRight className="w-3.5 h-3.5" />
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : !isFetching ? (
                                <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-sm text-gray-400">
                                    Nenhum lead encontrado.
                                </div>
                            ) : null}
                        </div>
                    )}
                </div>
            )}

            <ViewLeadModal open={isViewModalOpen} onOpenChange={setIsViewModalOpen} leadId={selectedLeadId} />
            <SyncLeadsDialog
                open={isSyncDialogOpen} onOpenChange={setIsSyncDialogOpen}
                pages={metaPagesData || []}
                onSyncComplete={() => { queryClient.invalidateQueries({ queryKey: ['leads'] }); queryClient.invalidateQueries({ queryKey: ['lead-stats'] }); }}
                onBackgroundSync={taskId => { setActiveSyncTaskId(taskId); setIsSyncDialogOpen(false); }}
            />
            {activeSyncTaskId && <FloatingSyncStatus taskId={activeSyncTaskId} onClose={() => setActiveSyncTaskId(null)} />}
        </div>
    );
}
